import { type RouteTrelloMeta, ROUTES_TRELLO_MAP } from './routesTrelloMap';
import { type VersionManifest } from './useAppVersionTelemetry';

/**
 * Gera string no formato Markdown com os dados de homologação e diagnóstico.
 */
export function generateMarkdownReport(
  meta: RouteTrelloMeta,
  local: VersionManifest,
  remote: VersionManifest | null,
  viewport: { width: number; height: number }
): string {
  const isStale = remote?.version && remote.version !== local.version;
  const isMobile = viewport.width < 768;

  return `# Relatório de Auditoria & Homologação de Rota
**Sistema:** Aluno Conectado — Plataforma Educacional Integrada  
**Data de Emissão:** ${new Date().toLocaleString('pt-BR')}  
**Assinatura Digital:** \`${local.gitCommit}\` | \`${local.version}\`

---

## 1. Metadados do Build & Ambiente de Teste

| Campo | Valor Local | Servidor Remoto (CDN) | Status |
| :--- | :--- | :--- | :--- |
| **Versão Semântica** | \`${local.version}\` | \`${remote?.version || 'N/A'}\` | ${isStale ? '⚠️ **DIVERGÊNCIA (OBSOLETO)**' : '✅ **SINCRONIZADO**'} |
| **Git Commit** | \`${local.gitCommit}\` | \`${remote?.gitCommit || 'N/A'}\` | ${local.gitCommit === remote?.gitCommit ? '✅ Idêntico' : '⚠️ Divergente'} |
| **Git Branch** | \`${local.gitBranch}\` | \`${remote?.gitBranch || 'N/A'}\` | - |
| **Data do Build** | ${local.buildTimestamp} | ${remote?.buildTimestamp || 'N/A'} | - |
| **Ambiente** | ${local.environment.toUpperCase()} | ${remote?.environment?.toUpperCase() || 'N/A'} | - |
| **Viewport do Testador** | ${viewport.width} x ${viewport.height} px | - | ${isMobile ? '📱 Mobile (< 768px)' : '💻 Desktop (>= 768px)'} |

---

## 2. Rastreabilidade Trello & Especificação da Rota

* **Rota Auditada:** \`${meta.routePattern}\` (${meta.routeName})
* **Cartão no Trello:** [${meta.trelloCard}](${meta.trelloUrl || '#'})
* **ID do Card:** \`${meta.trelloCardId || 'N/A'}\`
* **Sprint / Marco:** ${meta.sprint || 'Core'}
* **Status da Homologação:** **${meta.status}**
* **Última Auditoria Registrada:** ${meta.lastAuditDate}

### Decisão Arquitetural & Comportamento Esperado
> ${meta.lastDecision}

### Recursos e Componentes Validados
${meta.testedFeatures.map((feat) => `- [x] ${feat}`).join('\n')}

${meta.acBlocks && meta.acBlocks.length > 0 ? `\n### Blocos de Código Auditados (Blocos AC)\n${meta.acBlocks.map((b) => `- \`${b}\``).join('\n')}` : ''}

${meta.relatedTrelloCards && meta.relatedTrelloCards.length > 0 ? `
---

## 3. Discussões & Histórico de Comentários do Trello

${meta.relatedTrelloCards.map((card) => `### 📋 ${card.name} [${card.listName}]
* **Link Trello:** ${card.url}
* **Última Atividade:** ${new Date(card.dateLastActivity).toLocaleString('pt-BR')}
${card.labels.length > 0 ? `* **Etiquetas:** ${card.labels.map(l => `\`${l.name}\``).join(', ')}` : ''}
${card.attachments && card.attachments.length > 0 ? `* **Anexos / Capturas de Tela:**\n${card.attachments.map(a => `  - [${a.name}](${a.url}) (${a.mimeType || 'anexo'})`).join('\n')}` : ''}

${card.comments.length > 0 ? `#### Histórico Cronológico de Discussões:
${card.comments.map(c => `> **${c.author}** em *${new Date(c.date).toLocaleString('pt-BR')}*:\n> ${c.text.replace(/\n/g, '\n> ')}${c.images && c.images.length > 0 ? `\n>\n> *Capturas anexadas:* ${c.images.map(img => `[${img.alt}](${img.url})`).join(', ')}` : ''}`).join('\n\n')}` : '_Nenhum comentário registrado._'}
`).join('\n\n')}
` : ''}

---

## ${meta.relatedTrelloCards && meta.relatedTrelloCards.length > 0 ? '4' : '3'}. Parecer de Integridade e Cache
${
  isStale
    ? `> ⚠️ **ALERTA DE FALSO POSITIVO:** O navegador do testador executou os testes sob a versão **${local.version}**, mas o servidor já possui a versão **${remote?.version}**. É obrigatório purgar os caches locais para revalidação.`
    : `> ✅ **TESTE VÁLIDO & HOMOLOGADO:** Os testes foram executados contra a versão mais recente em produção/staging, sem interferência de caches legados.`
}

---
*Relatório gerado automaticamente pelo Sistema de Telemetria do Aluno Conectado.*
`;
}

/**
 * Dispara o download do relatório em formato Markdown (.md).
 */
export function downloadMarkdownReport(
  meta: RouteTrelloMeta,
  local: VersionManifest,
  remote: VersionManifest | null,
  viewport: { width: number; height: number }
) {
  const content = generateMarkdownReport(meta, local, remote, viewport);
  const blob = new Blob([content], { type: 'text/markdown;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  const sanitizedRoute = meta.routePattern.replace(/\//g, '_').replace(/^_/, '') || 'home';
  a.href = url;
  a.download = `auditoria_${sanitizedRoute}_${local.version}_${local.gitCommit}.md`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

/**
 * Gera documento HTML standalone completo e interativo.
 */
export function generateHtmlReport(
  meta: RouteTrelloMeta,
  local: VersionManifest,
  remote: VersionManifest | null,
  viewport: { width: number; height: number }
): string {
  const isStale = remote?.version && remote.version !== local.version;
  const isMobile = viewport.width < 768;

  return `<!DOCTYPE html>
<html lang="pt-BR">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>Relatório de Auditoria — ${meta.routeName} (${local.version})</title>
  <style>
    :root {
      --bg: #0f172a;
      --card: #1e293b;
      --text: #f8fafc;
      --muted: #94a3b8;
      --border: #334155;
      --accent: #3b82f6;
      --success: #10b981;
      --danger: #ef4444;
      --purple: #a855f7;
    }
    * { box-sizing: border-box; margin: 0; padding: 0; }
    body {
      font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif;
      background: var(--bg);
      color: var(--text);
      line-height: 1.5;
      padding: 30px 20px;
    }
    .container { max-width: 860px; margin: 0 auto; }
    header {
      border-bottom: 1px solid var(--border);
      padding-bottom: 20px;
      margin-bottom: 24px;
      display: flex;
      justify-content: space-between;
      align-items: flex-start;
      flex-wrap: wrap;
      gap: 16px;
    }
    h1 { font-size: 1.5rem; font-weight: 800; color: #fff; }
    .subtitle { font-size: 0.85rem; color: var(--muted); margin-top: 4px; }
    .badge {
      display: inline-flex;
      align-items: center;
      gap: 6px;
      font-size: 0.75rem;
      font-weight: 700;
      padding: 4px 10px;
      border-radius: 9999px;
      background: rgba(59, 130, 246, 0.2);
      color: #93c5fd;
      border: 1px solid rgba(59, 130, 246, 0.4);
    }
    .badge-success { background: rgba(16, 185, 129, 0.2); color: #6ee7b7; border-color: rgba(16, 185, 129, 0.4); }
    .badge-danger { background: rgba(239, 68, 68, 0.2); color: #fca5a5; border-color: rgba(239, 68, 68, 0.4); }
    .badge-purple { background: rgba(168, 85, 247, 0.2); color: #d8b4fe; border-color: rgba(168, 85, 247, 0.4); }
    
    .grid { display: grid; grid-template-columns: 1fr 1fr; gap: 16px; margin-bottom: 20px; }
    @media (max-width: 640px) { .grid { grid-template-columns: 1fr; } }
    
    .card {
      background: var(--card);
      border: 1px solid var(--border);
      border-radius: 12px;
      padding: 16px;
    }
    .card-title {
      font-size: 0.8rem;
      font-weight: 700;
      text-transform: uppercase;
      letter-spacing: 0.05em;
      color: var(--muted);
      margin-bottom: 12px;
      display: flex;
      align-items: center;
      justify-content: space-between;
    }
    .row {
      display: flex;
      justify-content: space-between;
      font-size: 0.82rem;
      padding: 6px 0;
      border-bottom: 1px dashed rgba(255, 255, 255, 0.08);
    }
    .row:last-child { border-bottom: none; }
    .row-val { font-family: monospace; font-weight: 700; color: #fff; }
    
    .alert-box {
      padding: 14px;
      border-radius: 10px;
      margin-bottom: 20px;
      font-size: 0.85rem;
    }
    .alert-box.success { background: rgba(16, 185, 129, 0.15); border: 1px solid var(--success); color: #a7f3d0; }
    .alert-box.danger { background: rgba(239, 68, 68, 0.15); border: 1px solid var(--danger); color: #fecaca; }
    
    .decision-box {
      background: rgba(168, 85, 247, 0.1);
      border: 1px solid rgba(168, 85, 247, 0.3);
      border-radius: 10px;
      padding: 14px;
      margin-top: 12px;
      font-size: 0.84rem;
      line-height: 1.5;
    }
    .features-list {
      display: flex;
      flex-wrap: wrap;
      gap: 6px;
      margin-top: 10px;
    }
    .feature-tag {
      background: rgba(255, 255, 255, 0.08);
      border: 1px solid var(--border);
      border-radius: 6px;
      font-size: 0.72rem;
      padding: 3px 8px;
    }
    .print-btn {
      background: var(--accent);
      color: #fff;
      border: none;
      padding: 8px 16px;
      font-size: 0.82rem;
      font-weight: 700;
      border-radius: 8px;
      cursor: pointer;
    }
    .print-btn:hover { background: #2563eb; }
    @media print {
      body { background: #fff; color: #000; padding: 0; }
      .container { max-width: 100%; }
      .print-btn { display: none; }
      .card { background: #fff; border: 1px solid #ccc; color: #000; }
      .row-val { color: #000; }
      .card-title { color: #555; }
      .alert-box.success { background: #f0fdf4; border-color: #86efac; color: #166534; }
      .alert-box.danger { background: #fef2f2; border-color: #fca5a5; color: #991b1b; }
      .decision-box { background: #faf5ff; border-color: #d8b4fe; color: #581c87; }
      .badge { background: #e0e7ff; color: #3730a3; border-color: #a5b4fc; }
    }
  </style>
</head>
<body>
  <div class="container">
    <header>
      <div>
        <h1>Relatório de Homologação & Auditoria</h1>
        <p class="subtitle">Plataforma Aluno Conectado • Rastreabilidade Trello & Assinatura de Versão</p>
      </div>
      <div>
        <button class="print-btn" onclick="window.print()">🖨️ Imprimir / Salvar em PDF</button>
      </div>
    </header>

    ${
      isStale
        ? `<div class="alert-box danger">
            <strong>⚠️ ATENÇÃO: DIVERGÊNCIA DE VERSÃO (CACHE OBSOLETO)</strong><br>
            O cliente executou os testes na versão <strong>${local.version} (${local.gitCommit})</strong>, mas a versão mais recente na CDN é <strong>${remote?.version || 'Atual'}</strong>. Risco de falso positivo.
          </div>`
        : `<div class="alert-box success">
            <strong>✅ HOMOLOGAÇÃO VALIDADA EM PRODUÇÃO/STAGING</strong><br>
            A versão do cliente <strong>${local.version} (${local.gitCommit})</strong> é 100% idêntica ao build ativo no servidor. Teste fidedigno.
          </div>`
    }

    <div class="grid">
      <!-- Card Metadados -->
      <div class="card">
        <div class="card-title">
          <span>Metadados do Build</span>
          <span class="badge">${local.version}</span>
        </div>
        <div class="row">
          <span style="color:var(--muted)">Git Commit:</span>
          <span class="row-val">${local.gitCommit}</span>
        </div>
        <div class="row">
          <span style="color:var(--muted)">Branch:</span>
          <span class="row-val">${local.gitBranch}</span>
        </div>
        <div class="row">
          <span style="color:var(--muted)">Build Timestamp:</span>
          <span class="row-val" style="font-size:0.75rem">${local.buildTimestamp}</span>
        </div>
        <div class="row">
          <span style="color:var(--muted)">Ambiente:</span>
          <span class="row-val">${local.environment.toUpperCase()}</span>
        </div>
      </div>

      <!-- Card Ambiente do Testador -->
      <div class="card">
        <div class="card-title">
          <span>Ambiente do Testador</span>
          <span class="badge ${isMobile ? 'badge-purple' : 'badge-success'}">${isMobile ? '📱 Mobile' : '💻 Desktop'}</span>
        </div>
        <div class="row">
          <span style="color:var(--muted)">Resolução de Tela:</span>
          <span class="row-val">${viewport.width} x ${viewport.height} px</span>
        </div>
        <div class="row">
          <span style="color:var(--muted)">Status de Cache:</span>
          <span class="row-val" style="color: ${isStale ? 'var(--danger)' : 'var(--success)'}">${isStale ? 'Desatualizado' : 'Sincronizado'}</span>
        </div>
        <div class="row">
          <span style="color:var(--muted)">Versão Servidor:</span>
          <span class="row-val" style="font-size:0.75rem">${remote?.version || 'Checando...'}</span>
        </div>
        <div class="row">
          <span style="color:var(--muted)">Data da Emissão:</span>
          <span class="row-val" style="font-size:0.75rem">${new Date().toLocaleDateString('pt-BR')}</span>
        </div>
      </div>
    </div>

    <!-- Card Rota & Trello -->
    <div class="card" style="margin-bottom: 20px;">
      <div class="card-title">
        <span>Rastreabilidade da Rota & Trello</span>
        <span class="badge badge-purple">${meta.status}</span>
      </div>
      <div class="row">
        <span style="color:var(--muted)">Rota Auditada:</span>
        <span class="row-val" style="color:#60a5fa">${meta.routePattern}</span>
      </div>
      <div class="row">
        <span style="color:var(--muted)">Nome da Tela:</span>
        <span class="row-val">${meta.routeName}</span>
      </div>
      <div class="row">
        <span style="color:var(--muted)">Cartão no Trello:</span>
        <span class="row-val">${meta.trelloCard}</span>
      </div>
      <div class="row">
        <span style="color:var(--muted)">Sprint / Auditoria:</span>
        <span class="row-val">${meta.sprint || 'Core'} (${meta.lastAuditDate})</span>
      </div>

      <div class="decision-box">
        <strong>Decisão Arquitetural / Comportamento Esperado:</strong><br>
        ${meta.lastDecision}
      </div>

      <div style="margin-top: 14px;">
        <span style="font-size:0.75rem; color:var(--muted); font-weight:700; text-transform:uppercase;">Recursos Validados no Card:</span>
        <div class="features-list">
          ${meta.testedFeatures.map((f) => `<span class="feature-tag">✓ ${f}</span>`).join('')}
        </div>
      </div>
    </div>

    ${meta.relatedTrelloCards && meta.relatedTrelloCards.length > 0 ? `
    <!-- Card Discussões Trello -->
    <div class="card" style="margin-bottom: 20px;">
      <div class="card-title">
        <span>Histórico & Discussões do Trello</span>
        <span class="badge badge-purple">${meta.relatedTrelloCards.reduce((acc, c) => acc + c.commentsCount, 0)} Comentários</span>
      </div>

      ${meta.relatedTrelloCards.map((card) => `
        <div style="background:rgba(255,255,255,0.03); border:1px solid var(--border); border-radius:10px; padding:12px; margin-bottom:12px;">
          <div style="display:flex; justify-content:space-between; align-items:center; margin-bottom:6px;">
            <strong style="color:#fff; font-size:0.85rem;">📋 ${card.name}</strong>
            <span class="badge badge-purple">${card.listName}</span>
          </div>
          <p style="font-size:0.75rem; color:var(--muted); margin-bottom:8px;">
            Última atividade: ${new Date(card.dateLastActivity).toLocaleString('pt-BR')} • <a href="${card.url}" target="_blank" style="color:var(--accent); text-decoration:none;">Abrir no Trello ↗</a>
          </p>

          ${card.comments.length > 0 ? `
            <div style="border-left:2px solid var(--purple); padding-left:10px; margin-top:8px;">
              ${card.comments.map((comm) => `
                <div style="background:rgba(0,0,0,0.2); border:1px solid rgba(255,255,255,0.05); border-radius:6px; padding:8px; margin-bottom:6px;">
                  <div style="display:flex; justify-content:space-between; font-size:0.72rem; color:var(--muted); margin-bottom:3px;">
                    <strong style="color:#e2e8f0;">${comm.author}</strong>
                    <span>${new Date(comm.date).toLocaleString('pt-BR')}</span>
                  </div>
                  <p style="font-size:0.78rem; color:#cbd5e1; white-space:pre-wrap; line-height:1.4;">${comm.text}</p>
                </div>
              `).join('')}
            </div>
          ` : '<p style="font-size:0.72rem; color:var(--muted); font-style:italic;">Nenhum comentário registrado ainda.</p>'}
        </div>
      `).join('')}
    </div>
    ` : ''}

    <footer style="text-align: center; font-size: 0.75rem; color: var(--muted); border-top: 1px solid var(--border); padding-top: 16px;">
      Plataforma Aluno Conectado • Assinatura Criptográfica: <code>${local.gitCommit}</code>
    </footer>
  </div>
</body>
</html>`;
}

/**
 * Dispara download do arquivo HTML.
 */
export function downloadHtmlReport(
  meta: RouteTrelloMeta,
  local: VersionManifest,
  remote: VersionManifest | null,
  viewport: { width: number; height: number }
) {
  const content = generateHtmlReport(meta, local, remote, viewport);
  const blob = new Blob([content], { type: 'text/html;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  const sanitizedRoute = meta.routePattern.replace(/\//g, '_').replace(/^_/, '') || 'home';
  a.href = url;
  a.download = `auditoria_${sanitizedRoute}_${local.version}_${local.gitCommit}.html`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

/**
 * Abre janela popup estilizada pronta para salvar como PDF (window.print).
 */
export function printAuditReport(
  meta: RouteTrelloMeta,
  local: VersionManifest,
  remote: VersionManifest | null,
  viewport: { width: number; height: number }
) {
  const content = generateHtmlReport(meta, local, remote, viewport);
  const printWindow = window.open('', '_blank');
  if (printWindow) {
    printWindow.document.write(content);
    printWindow.document.close();
    printWindow.focus();
    setTimeout(() => {
      printWindow.print();
    }, 300);
  }
}
